import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import { ArrowRight } from "lucide-react";

const heroImages = [
  {
    src: "/images/coastal-home-after.jpg",
    alt: "Exterior painters in Orlando Florida completed residential home with warm greige stucco finish",
  },
  {
    src: "/images/casselberry-exterior-after.jpg",
    alt: "House painters in Lake Nona area completed modern gray exterior with crisp white trim",
  },
  {
    src: "/images/contemporary-home-after.jpg",
    alt: "Exterior painting companies in Longwood Florida delivered stunning white home with charcoal accents",
  },
  {
    src: "/images/commercial-after.jpg",
    alt: "Commercial painting companies in Seminole County Florida completed professional building branding",
  },
];

export const Hero = () => {
  const [current, setCurrent] = useState(0);

  const advance = useCallback(() => {
    setCurrent((prev) => (prev + 1) % heroImages.length);
  }, []);

  useEffect(() => {
    const id = setInterval(advance, 5000);
    return () => clearInterval(id);
  }, [advance]);

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex flex-col overflow-hidden"
    >
      {/* Slideshow background */}
      <div className="absolute inset-0 z-0">
        {heroImages.map((img) => (
          <img
            key={img.alt}
            src={img.src}
            alt={img.alt}
            className="absolute inset-0 w-full h-full object-cover"
            style={{
              opacity: heroImages.indexOf(img) === current ? 1 : 0,
              transition: "opacity 1.2s ease-in-out",
            }}
          />
        ))}
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/15 to-black/55" />
      </div>

      {/* Content */}
      <div className="relative z-10 flex-1 flex flex-col max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-12 pt-28 sm:pt-36 pb-10 sm:pb-12">
        {/* Headline */}
        <div className="flex-1 flex items-start">
          <h1
            data-testid="hero-heading"
            className="font-heading text-[2.5rem] leading-[1.1] sm:text-6xl md:text-7xl lg:text-[5.5rem] font-semibold sm:leading-[1.05] tracking-tight text-white max-w-2xl animate-fade-in"
            style={{ textShadow: "0 2px 24px rgba(0,0,0,0.25)" }}
          >
            Orlando's Trusted Painting Professionals
          </h1>
        </div>

        {/* Bottom: description + CTA (right-aligned on desktop) */}
        <div className="flex flex-col sm:items-end sm:text-right animate-fade-in animate-delay-300">
          <p
            className="text-white/75 text-sm sm:text-base md:text-lg leading-relaxed mb-5 sm:mb-6 max-w-sm"
            style={{ textShadow: "0 1px 8px rgba(0,0,0,0.3)" }}
          >
            10+ years of expert interior & exterior painting, popcorn ceiling removal, and specialty coatings for Central Florida homes.
          </p>
          <Link to="/quote">
            <Button
              data-testid="hero-quote-btn"
              className="group rounded-full bg-brand-orange text-white font-heading font-medium text-sm uppercase tracking-wide h-11 sm:h-12 px-7 sm:px-8 hover:bg-[#d9432a] transition-all duration-300 shadow-lg shadow-brand-orange/20 w-full sm:w-auto"
            >
              Get Free Estimate
              <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-0.5 transition-transform duration-300" />
            </Button>
          </Link>
        </div>

        {/* Slide indicators */}
        <div className="flex gap-2 mt-6 sm:mt-8 sm:justify-end">
          {heroImages.map((img, idx) => (
            <button
              key={img.alt}
              aria-label={`Slide ${idx + 1}`}
              onClick={() => setCurrent(idx)}
              className={`h-1 rounded-full transition-all duration-500 ${
                idx === current
                  ? "bg-brand-orange w-8"
                  : "bg-white/30 w-4 hover:bg-white/50"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};
